import { useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import moment from "moment";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";

import Badge from "components/v2/Badge/Badge";
import usePermissions from "hooks/usePermissions";
import { renderAddressV2InfoForHeaders } from "helpers/addressV2Helpers";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { ReactComponent as BoltIcon } from "assets/images/icons/BoltBlue.svg";
import { ReactComponent as EditIcon } from "assets/images/iconsV2/edit.svg";
import { quoteRequestQueryKeys } from "api/queries/quotes/quoteRequestQueryKeys";
import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import testIds from "constants/testIds";
import EditTimersDialog from "pages/shipper/ShipmentQuotes/components/EditTimersDialog";
import TopNavLocationDetail from "./TopNavLocationDetail";
import TopNavCreatedBy from "./TopNavCreatedBy";

interface TopNavQuoteRequestDetailsProps {
  data: any;
}

function TopNavQuoteRequestDetails({ data }: TopNavQuoteRequestDetailsProps) {
  const intl = useIntl();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { data: shipmentBasicData } = useGetShipmentBasic(id!, {
    enabled: !!id,
    refetchOnMount: false,
  });

  const { permissions } = usePermissions();
  const { user_type } = JSON.parse(permissions || "{}");
  const isShipper = user_type === "shipper";
  const isGuestUser = Cookies.get("is_guest") === "true";

  const [showEditTimersDialog, setShowEditTimersDialog] = useState(false);

  const {
    confirmation_date,
    confirmation_time,
    confirmation_date_remaining_time_display,
    confirmation_date_seconds_remaining,
    quoting_deadline_date,
    quoting_deadline_time,
    quoting_deadline_remaining_time_display,
    quoting_deadline_seconds_remaining,
    payment_location,
    shipment_id,
    requestor,
    requestor_email,
  } = data?.organization || {};

  const schedule = data?.schedule || {};
  const { address_v2 } = schedule;
  const hasAddressV2 = Object.keys(address_v2 ?? {}).some(
    (key) => key !== "pickup_is_port_address" && key !== "delivery_is_port_address",
  );

  const { renderPickupAddress, renderDeliveryAddress } = renderAddressV2InfoForHeaders({ data, useRevampedDesign: true });
  const { quick_request_reference } = data?.quick_request_details?.[0] || {};
  const isConsolidated = data?.quick_request_details?.length > 1;

  const requestStatus = shipmentBasicData?.organization?.request_status;
  const isCanceled = requestStatus === "Canceled" || requestStatus === "Auto canceled";
  const isQuoteRequestBooked = !data?.available_for_booking;

  const hideEditDatesButton = [
    "In transit",
    "Incomplete",
    "Delayed",
    "Delivered",
    "Booked",
    "Archive",
    "Canceled",
    "Auto canceled",
  ].includes(requestStatus as string);

  const fetchData = () => {
    queryClient.invalidateQueries({ queryKey: quoteRequestQueryKeys.quoteDetails(id!) });
    queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentBasicDetails(id!) });
  };

  const hasDetails =
    schedule.pickup_address_name || schedule.stop1_address_name || requestor || quoting_deadline_remaining_time_display;
  if (!hasDetails) return null;

  const getTimerBadgeColor = (seconds: number): "success" | "error" | "warning" => {
    if (seconds < 0) return "error";
    if (seconds < 3600) return "warning";
    return "success";
  };

  const getTimerStatusLabel = () => {
    if (isCanceled) {
      return requestStatus === "Auto canceled"
        ? intl.formatMessage({ id: "general.autoCanceled", defaultMessage: "Auto-canceled" })
        : intl.formatMessage({ id: "general.canceled", defaultMessage: "Canceled" });
    }
    if (isQuoteRequestBooked) {
      return intl.formatMessage({ id: "quote-request.closed", defaultMessage: "Closed" });
    }
    return "";
  };

  const renderTimerBadge = (secondsRemaining: number, timeDisplay: string) => {
    if (isCanceled || isQuoteRequestBooked) {
      return (
        <div className="w-fit">
          <Badge color="gray" type="status-badge" size="sm" label={getTimerStatusLabel()} />
        </div>
      );
    }
    return (
      <div className="w-fit">
        <Badge
          color={getTimerBadgeColor(secondsRemaining)}
          type="status-badge"
          size="sm"
          label={
            secondsRemaining < 0
              ? intl.formatMessage({ id: "quote-request.deadlinePassed", defaultMessage: "Passed" })
              : timeDisplay
          }
        />
      </div>
    );
  };

  return (
    <div className="topnav-v2__details" data-testid={testIds.TOPNAV_V2_QUOTE_REQUEST_DETAILS}>
      <EditTimersDialog
        fetchData={fetchData}
        isOpen={showEditTimersDialog}
        close={() => setShowEditTimersDialog(false)}
        requestId={shipment_id}
        pickupDate={schedule.pickup_date}
        requestTimers={{
          confirmation_date,
          confirmation_time: confirmation_time?.slice(0, -3),
          quoting_deadline_date,
          quoting_deadline_time: quoting_deadline_time?.slice(0, -3),
        }}
        quoting_deadline_seconds_remaining={quoting_deadline_seconds_remaining}
      />
      {/* Timers */}
      <div className="topnav-v2__timers" data-testid={testIds.TOPNAV_V2_QUOTE_REQUEST_TIMERS_CONTAINER}>
        <div className="topnav-v2__timer" data-testid={testIds.TOPNAV_V2_QUOTE_BY_CONTAINER}>
          <div className="topnav-v2__timer-content">
            <span className="topnav-v2__detail-label" data-testid={testIds.TOPNAV_V2_QUOTE_BY_LABEL}>
              <FormattedMessage id="new-request.quote-confirmation.schedule.quoteBy" defaultMessage="Quote by" />
            </span>
            <OverlayTrigger
              placement="bottom"
              delay={{ show: 250, hide: 0 }}
              overlay={
                <Tooltip id="quoting-deadline-tooltip">
                  {moment(new Date(`${quoting_deadline_date} ${quoting_deadline_time}`)).format("DD MMM YYYY, HH:mm")}
                </Tooltip>
              }
            >
              <div data-testid={testIds.TOPNAV_V2_QUOTE_BY_VALUE}>
                {renderTimerBadge(quoting_deadline_seconds_remaining, quoting_deadline_remaining_time_display)}
              </div>
            </OverlayTrigger>
          </div>
          {!hideEditDatesButton && isShipper && !isGuestUser && (
            <button
              type="button"
              className="topnav-v2__edit-timers-btn"
              onClick={() => setShowEditTimersDialog(true)}
              data-testid={testIds.TOPNAV_V2_EDIT_TIMERS_BUTTON}
            >
              <EditIcon width={18} height={18} data-testid={testIds.TOPNAV_V2_EDIT_TIMERS_ICON} />
            </button>
          )}
        </div>
        <div className="topnav-v2__timer" data-testid={testIds.TOPNAV_V2_CONFIRM_BY_CONTAINER}>
          <div className="topnav-v2__timer-content">
            <span className="topnav-v2__detail-label" data-testid={testIds.TOPNAV_V2_CONFIRM_BY_LABEL}>
              <FormattedMessage id="new-request.quote-confirmation.schedule.confirmBy" defaultMessage="Confirm by" />
            </span>
            <OverlayTrigger
              placement="bottom"
              delay={{ show: 250, hide: 0 }}
              overlay={
                <Tooltip id="confirmation-deadline-tooltip">
                  {moment(new Date(`${confirmation_date} ${confirmation_time}`)).format("DD MMM YYYY, HH:mm")}
                </Tooltip>
              }
            >
              <div data-testid={testIds.TOPNAV_V2_CONFIRM_BY_VALUE}>
                {renderTimerBadge(confirmation_date_seconds_remaining, confirmation_date_remaining_time_display)}
              </div>
            </OverlayTrigger>
          </div>
          {!hideEditDatesButton && isShipper && !isGuestUser && (
            <button
              type="button"
              className="topnav-v2__edit-timers-btn"
              onClick={() => setShowEditTimersDialog(true)}
              data-testid={testIds.TOPNAV_V2_EDIT_TIMERS_BUTTON}
            >
              <EditIcon width={18} height={18} data-testid={testIds.TOPNAV_V2_EDIT_TIMERS_ICON} />
            </button>
          )}
        </div>
      </div>

      {/* Pickup */}
      {schedule.pickup_address_name && (
        <TopNavLocationDetail
          type="pickup"
          addressName={schedule.pickup_address_name}
          addressFields={[
            schedule.pickup_zipcode,
            schedule.pickup_city,
            schedule.pickup_airport,
            schedule.pickup_seaport,
            schedule.pickup_country_code,
          ]}
          copyFields={[
            schedule.pickup_address_name,
            schedule.pickup_city,
            schedule.pickup_airport,
            schedule.pickup_seaport,
            schedule.pickup_country_code,
            schedule.pickup_zipcode,
          ]}
          date={schedule.pickup_date}
          time={schedule.pickup_time}
          endDate={schedule.pickup_end_date}
          endTime={schedule.pickup_end_time}
          hasAddressV2={hasAddressV2}
          renderAddressV2={renderPickupAddress}
          automationIdPrefix="topnav-v2-quote-request-pickup"
        />
      )}

      {/* Delivery */}
      {schedule.stop1_address_name && (
        <TopNavLocationDetail
          type="delivery"
          addressName={schedule.stop1_address_name}
          addressFields={[
            schedule.stop1_zipcode,
            schedule.stop1_city,
            schedule.stop1_airport,
            schedule.stop1_seaport,
            schedule.stop1_country_code,
          ]}
          copyFields={[
            schedule.stop1_address_name,
            schedule.stop1_city,
            schedule.stop1_airport,
            schedule.stop1_seaport,
            schedule.stop1_country_code,
            schedule.stop1_zipcode,
          ]}
          date={schedule.stop1_date}
          time={schedule.stop1_time}
          endDate={schedule.stop1_end_date}
          endTime={schedule.stop1_end_time}
          isAsap={schedule.stop1_datetime === "ASAP"}
          hasAddressV2={hasAddressV2}
          renderAddressV2={renderDeliveryAddress}
          automationIdPrefix="topnav-v2-quote-request-delivery"
        />
      )}

      {/* Created by */}
      {requestor && (
        <TopNavCreatedBy
          name={requestor}
          email={requestor_email}
          subtitle={payment_location}
          link={
            quick_request_reference && !isConsolidated
              ? {
                  to: `/shipment/${quick_request_reference}/quick`,
                  label: quick_request_reference,
                  icon: <BoltIcon height={18} width={18} />,
                }
              : undefined
          }
          automationIdPrefix="topnav-v2-quote-request-created-by"
        />
      )}
    </div>
  );
}

export default TopNavQuoteRequestDetails;
